import {
  BUILTIN_ALTERNATIVE_SHAPES,
  DEFAULT_PREFERRED_ALTERNATIVE_IDS,
  type AlternativeShape,
} from '@/music/alternativeShapes';
import { CHORD_TYPES, chordSymbol, chordToneName, type ChordTypeId } from '@/music/chords';
import { parseKey, pitchClassOf, sharpName, spellNoteInKey } from '@/music/notes';
import type { Bar, ChordEvent, MelodyNote, Song } from '@/music/song';
import { guitarPinOf, isGuitarPin, pinFor, type LanePins } from '@/music/voicingPins';
import type { StringSet } from '@/music/tuning';
import type { VoicingFamilySelection } from '@/music/voicingFamilies';
import {
  optimizeVoiceLeadingGlobal,
  type OptimizableChord,
  type OptimizedChord,
} from '@/music/voiceLeading';

export type MelodyRoleKind = 'chord-tone' | 'extension' | 'movement';

export interface ArrangementMelodyStep {
  id: string;
  note: string;
  midi: number;
  beat: number;
  duration: number;
  role: string;
  kind: MelodyRoleKind;
}

/** One generated grip at a real harmony change. The source chord remains the
 * chart's harmonic truth; chordType may be a four-note tension realization. */
export interface ArrangementChord extends OptimizableChord {
  id: string;
  barIndex: number;
  barId: string;
  beat: number;
  durationBeats: number;
  /** The authored chord's onset beat (`beat` is the note's under every-note). */
  chordBeat: number;
  /** Under every-note density, the melody note's onset beat in its bar. */
  noteBeat?: number;
  /** This event's position key in the melody lane (voicingPins.ts). */
  pinKey?: string;
  sourceChordType: ChordTypeId;
  sourceSymbol: string;
  voicedSymbol: string;
  displayRoot: string;
  melody: ArrangementMelodyStep[];
}

/**
 * How many grips the chord melody writes. `changes` (the default) voices one
 * grip per authored harmony change with the melody note sounding at that
 * change on top — every later note in the span is annotation, not an attack.
 * `every-note` (user request, Sep 2026: "an arrange option that applies a
 * chord to every note") puts a grip under EVERY melody note, the block-chord
 * texture: each note becomes its own event carrying the harmony in force,
 * realised through the same melody-aware tension table, so the voice-leading
 * search still holds every written interval on top. A tie's far side is the
 * same note and takes no new attack (unless the harmony changes under it),
 * and a bar with notes but no symbol carries the last harmony forward.
 */
export type ChordMelodyDensity = 'changes' | 'every-note';

export interface ArrangementVoicingOptions {
  stringSet: StringSet;
  density?: ChordMelodyDensity;
  voicingFamily?: VoicingFamilySelection;
  startingInversion?: number;
  avoidB9?: boolean;
  freeStringSet?: boolean;
  /** Saved alternatives supplement ordinary family candidates. Defaults to the
   * built-in library; strict drop-voicing studies pass an empty list. */
  alternativeShapes?: readonly AlternativeShape[];
  preferredAlternativeIds?: readonly string[];
  /** The melody lane's pins (voicingPins.ts), by position key, and the key
   * of an event: the written bar (by id), the chord's onset beat and, under
   * every-note, the note's. The caller owns the bar-id → index map. */
  pins?: LanePins;
  pinKeyOf?: (barId: string, chordBeat: number, noteBeat?: number) => string | undefined;
}

function soundingNoteAt(melody: MelodyNote[], beat: number): MelodyNote | undefined {
  return melody.find(
    (note) =>
      !note.rest &&
      !note.slash &&
      !note.grace &&
      beat >= note.beat &&
      beat < note.beat + Math.max(0, note.duration),
  );
}

function isChordTone(type: ChordTypeId, interval: number): boolean {
  return CHORD_TYPES[type].intervals.some((tone) => ((tone % 12) + 12) % 12 === interval);
}

function extensionName(interval: number): string {
  const names: Record<number, string> = {
    1: '♭9',
    2: '9',
    3: '♯9',
    5: '11',
    6: '♯11',
    8: '♭13',
    9: '13',
  };
  return names[interval] ?? String(interval);
}

function melodyStep(
  note: MelodyNote,
  chord: ChordEvent,
  key: ReturnType<typeof parseKey>,
  isOpeningNote: boolean,
): ArrangementMelodyStep {
  const interval = ((note.midi % 12) - pitchClassOf(chord.root) + 12) % 12;
  const chordTone = isChordTone(chord.chordType, interval);
  return {
    id: note.id,
    note: note.spelledAs ?? spellNoteInKey(note.midi % 12, key),
    midi: note.midi,
    beat: note.beat,
    duration: note.duration,
    role: chordTone
      ? chordToneName(note.midi % 12, pitchClassOf(chord.root))
      : extensionName(interval),
    kind: chordTone
      ? 'chord-tone'
      : !isOpeningNote && note.duration <= 0.5
        ? 'movement'
        : 'extension',
  };
}

/** Use one of the catalogue's rootless four-note tension shapes when the
 * melody supplies that tension. This changes the generated grip, never the
 * authored chord symbol. */
function realizedChordType(
  chord: ChordEvent,
  lead: MelodyNote | undefined,
  avoidB9: boolean,
  catalogueFamily = false,
): ChordTypeId {
  if (!lead) return chord.chordType;
  const interval = ((lead.midi % 12) - pitchClassOf(chord.root) + 12) % 12;
  // The arranger's rule, not a filter: a melody sitting on the ROOT of a
  // major-7 (or minor-major-7) chord clashes with the 7 a semitone below in
  // EVERY four-note grip that tops it — which is why these were the grips
  // the avoid-b9 fallback kept re-admitting. Voice the 6th chord instead:
  // same function, root on top, no clash anywhere in the stack. Only while
  // Avoid b9 is ON — with it off, the written 7th stands.
  if (avoidB9 && interval === 0) {
    if (chord.chordType === 'maj7') return 'maj6';
    if (chord.chordType === 'maj9') return 'maj69';
    if (chord.chordType === 'minmaj7') return 'min6';
  }
  if (isChordTone(chord.chordType, interval)) return chord.chordType;

  // A finite grip collection cannot realize the drop engine's arbitrary
  // four-note replacement below. Name the same melody-led alteration before
  // asking the catalogue, so a written b13/#11/sus melody can select an
  // actual compatible grip without synthesizing any new fret pattern.
  if (catalogueFamily) {
    if (interval === 8 && chord.chordType === 'dom7') return 'dom7s5';
    if (interval === 6 && chord.chordType === 'dom7') return 'dom7b5';
    if (interval === 6 && chord.chordType === 'maj7') return 'maj7b5';
    if (interval === 5 && chord.chordType === 'dom7') return 'dom7sus4';
  }

  if (interval === 2) {
    const ninths: Partial<Record<ChordTypeId, ChordTypeId>> = {
      maj7: 'maj9',
      maj6: 'maj69',
      dom7: 'dom9',
      min7: 'min9',
      min6: 'min69',
      min7b5: 'min7b59',
    };
    return ninths[chord.chordType] ?? chord.chordType;
  }
  if (interval === 1) {
    if (chord.chordType === 'dom7') return 'dom7b9';
    if (chord.chordType === 'min7b5') return 'min7b5b9';
  }
  if (interval === 3 && chord.chordType === 'dom7') return 'dom7s9';
  if (interval === 9 && (chord.chordType === 'dom7' || chord.chordType === 'dom9')) {
    return 'dom13';
  }
  return chord.chordType;
}

/** Rootless tension formulae commonly omit the root and fifth. If the written
 * melody asks for an omitted tone, restore it and discard the fifth first —
 * the chord keeps its authored symbol while the top voice becomes playable.
 * One exception: a natural 11 over a major 3rd displaces the 3rd (the sus
 * convention), not the 5th — keeping both bakes an m2 clash into every grip. */
function melodyAwareVoicingNotes(
  root: string,
  chordType: ChordTypeId,
  lead: MelodyNote | undefined,
): string[] | undefined {
  if (!lead) return undefined;
  const rootPc = pitchClassOf(root);
  const target = ((lead.midi % 12) - rootPc + 12) % 12;
  const intervals = [...CHORD_TYPES[chordType].intervals];
  if (intervals.some((interval) => ((interval % 12) + 12) % 12 === target)) return undefined;

  const majorThird = intervals.findIndex((interval) => interval % 12 === 4);
  const fifth = intervals.findIndex((interval) => interval % 12 === 7);
  const rootTone = intervals.findIndex((interval) => interval % 12 === 0);
  const displaced =
    target === 5 && majorThird >= 0
      ? majorThird
      : fifth >= 0
        ? fifth
        : rootTone >= 0
          ? rootTone
          : 0;
  intervals.splice(displaced, 1);
  intervals.push(target);
  intervals.sort((a, b) => a - b);
  return intervals.map((interval) => sharpName((rootPc + interval) % 12));
}

function selectMelodySteps(bar: Bar, chord: ChordEvent, endBeat: number): MelodyNote[] {
  const melody = (bar.melody ?? []).filter((note) => !note.rest && !note.slash && !note.grace);
  const opening = soundingNoteAt(melody, chord.beat);
  const inSpan = melody.filter((note) => note.beat >= chord.beat && note.beat < endBeat);
  const ordered = [opening, ...inSpan]
    .filter((note): note is MelodyNote => !!note)
    .filter((note, index, all) => all.findIndex((other) => other.id === note.id) === index)
    .sort((a, b) => a.beat - b.beat);

  return ordered;
}

/** Build the arrangement events: one per authored harmony change (every
 * melody note in that harmonic span attached as annotation; movement notes
 * do not invent extra chord attacks or reharmonizations), or under
 * `every-note` one per melody note — see `ChordMelodyDensity`. */
export function arrangementSequence(
  song: Song,
  stringSet: StringSet,
  avoidB9 = true,
  density: ChordMelodyDensity = 'changes',
  pinning?: Pick<ArrangementVoicingOptions, 'pins' | 'pinKeyOf'>,
  voicingFamily?: VoicingFamilySelection,
): ArrangementChord[] {
  const key = parseKey(song.key);
  const sequence: ArrangementChord[] = [];
  let barIndex = 0;
  // Under every-note a bar with no symbol of its own plays the harmony
  // carried into it (the chart convention), so its notes still get grips —
  // and a melody-less one still gets the carried chord to comp through.
  let carried: ChordEvent | undefined;

  for (const section of song.sections) {
    for (const bar of section.bars) {
      let chords = [...bar.chords].sort((a, b) => a.beat - b.beat);
      if (density === 'every-note' && chords.length === 0 && carried) {
        chords = [{ ...carried, beat: 1, duration: bar.beats }];
      }
      const emit = (
        chord: ChordEvent,
        notes: MelodyNote[],
        beat: number,
        durationBeats: number,
        idSuffix: string,
      ) => {
        const lead = notes[0];
        const voicedType = realizedChordType(chord, lead, avoidB9, voicingFamily === 'joe-pass');
        const voicingNotes =
          voicingFamily === 'joe-pass'
            ? undefined
            : melodyAwareVoicingNotes(chord.root, voicedType, lead);
        const melody = notes.map((note, noteIndex) =>
          melodyStep(note, chord, key, noteIndex === 0),
        );
        const targetTopNote = melody[0]?.note ?? chord.targetTopNote;
        const targetTopMidi = melody[0]?.midi;
        const displayRoot = chord.root.replace(/b/g, '♭').replace(/#/g, '♯');
        const noteBeat = idSuffix ? lead?.beat : undefined;
        const pinKey = pinning?.pinKeyOf?.(bar.id, chord.beat, noteBeat);
        const pin = pinFor(
          pinning?.pins ? { 'melody.guitar': pinning.pins } : undefined,
          'melody.guitar',
          pinKey,
          voicedType,
        );

        sequence.push({
          id: `${section.id}.${bar.id}.${chord.beat}${idSuffix}`,
          barIndex,
          barId: bar.id,
          beat,
          chordBeat: chord.beat,
          ...(noteBeat != null ? { noteBeat } : {}),
          ...(pinKey ? { pinKey } : {}),
          ...(pin && isGuitarPin(pin) ? { pin: guitarPinOf(pin) } : {}),
          durationBeats: Math.max(0.25, durationBeats),
          root: chord.root,
          displayRoot,
          chordType: voicedType,
          sourceChordType: chord.chordType,
          sourceSymbol: chord.symbol ?? chordSymbol(chord.root, chord.chordType),
          voicedSymbol: chordSymbol(displayRoot, voicedType),
          stringSet,
          targetTopNote,
          targetTopMidi,
          contourGroup: `${section.id}.${bar.id}`,
          preferredInversion: chord.preferredInversion,
          ...(chord.bass ? { bass: chord.bass } : {}),
          voicingNotes,
          melody,
        });
      };

      chords.forEach((chord, chordIndex) => {
        const nextBeat = chords[chordIndex + 1]?.beat ?? bar.beats + 1;
        const notes = selectMelodySteps(bar, chord, nextBeat);
        if (density === 'every-note' && notes.length > 0) {
          // The far side of a tie is the same note: no new attack — except
          // as the span's opening, where the harmony has changed under it.
          const attacks = notes.filter(
            (note, k) => k === 0 || !(note.tie === 'stop' || note.tie === 'both'),
          );
          // A RUN of the same pitch is one grip (user, Sep 2026: "if there are
          // repeated notes just show one chord diagram"): the chord sounds
          // once under the re-articulated melody, and the sheet draws one
          // diagram spanning the run rather than the same grip four times.
          attacks.forEach((note, k) => {
            if (k > 0 && attacks[k - 1].midi === note.midi) return;
            const beat = Math.max(chord.beat, note.beat);
            let j = k + 1;
            while (j < attacks.length && attacks[j].midi === note.midi) j += 1;
            const next = attacks[j];
            const end = next ? Math.max(chord.beat, next.beat) : nextBeat;
            emit(chord, [note], beat, end - beat, `.${note.id}`);
          });
          return;
        }
        emit(chord, notes, chord.beat, nextBeat - chord.beat, '');
      });
      if (chords.length > 0) carried = chords[chords.length - 1];
      barIndex += 1;
    }
  }

  return sequence;
}

/** Build the exact melody-led guitar line used by both the Arrange canvas and
 * transport playback. Keeping this seam shared prevents the diagram lane and
 * the comping track from quietly choosing different chord realizations. */
export function buildArrangementVoicings(
  song: Song,
  options: ArrangementVoicingOptions,
): OptimizedChord<ArrangementChord>[] {
  const sequence = arrangementSequence(
    song,
    options.stringSet,
    options.avoidB9 ?? true,
    options.density ?? 'changes',
    options.pins || options.pinKeyOf
      ? { pins: options.pins, pinKeyOf: options.pinKeyOf }
      : undefined,
    options.voicingFamily,
  );
  return (
    optimizeVoiceLeadingGlobal(sequence, {
      voicingFamily: options.voicingFamily,
      startingInversion: options.startingInversion,
      avoidB9: options.avoidB9,
      freeStringSet: options.freeStringSet,
      alternativeShapes: options.alternativeShapes ?? BUILTIN_ALTERNATIVE_SHAPES,
      preferredAlternativeIds: options.preferredAlternativeIds ?? DEFAULT_PREFERRED_ALTERNATIVE_IDS,
      topNoteWeight: 1_000_000,
      // Anchor the line in the score's opening register when either string set
      // can play it, then allow later phrase-level octave displacement.
      topOpeningRegisterWeight: 10_000,
      // Preserve the melody pitch class, and PULL gently toward the written
      // octave. The contour terms below hold every written interval, so the
      // line can only re-register where the written pitch is unreachable —
      // but at zero this weight, a displaced stretch was merely ALLOWED back,
      // never rewarded, and with the free string set it would drift an octave
      // up for whole phrases because staying displaced was cheaper for the
      // hand. Swept over the 151-tune openbook corpus (cross-set): 10–25 is a
      // plateau that removes ~11% of octave-displaced notes with contour
      // breaks EXACTLY unchanged (242) and +2% movement; ≥50 starts trading
      // written intervals for octaves (242→249→268), which is the failure
      // mode zero was defending against. Mid-plateau, away from both cliffs:
      topRegisterWeight: 25,
      topContourWeight: 10_000,
      // Keep the written intervals across bars too. The high-fret cost below
      // may change string sets, but must not fold a melodic ascent into an
      // octave drop.
      topContourBoundaryWeight: 10_000,
      // A note no grip can top at its written pitch (G3 under the middle
      // set's open B) is folded an octave whatever we do, so the contour is
      // broken there by construction — the question is only whether the
      // phrase around it follows. Under the hard weight the note AFTER the
      // fold had to match the folded note's octave, and the whole phrase then
      // rode at +12 until the next fold: My One And Only Love (written
      // G3–C5) played bars 6–23 at frets 12–19 off two low notes. Soft here,
      // twice one chord's register pull (12 × 25): a lone reachable note
      // between two folds still rides with them (2 × 600 > 300), while a
      // phrase of two or more comes home (N × 300 > 600).
      topContourFoldWeight: 200,
      highFretWeight: 4,
      highFretThreshold: 12,
      allowOctaveShifts: true,
      // The opening grip must participate in the phrase search. Otherwise an
      // isolated low-position choice can force an immediate slide where a
      // same-position string crossing would be smoother.
      optimizeOpening: true,
    }) ?? []
  );
}
